import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Copy, Check, Gift, DollarSign, Users, Share2 } from 'lucide-react';

export default function ReferralPage() {
  const { authHeaders, API, user } = useAuth();
  const [referralInfo, setReferralInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [infoRes, histRes] = await Promise.all([
          axios.get(`${API}/referral/info`, authHeaders()),
          axios.get(`${API}/referral/history`, authHeaders())
        ]);
        setReferralInfo(infoRes.data);
        setHistory(histRes.data);
      } catch (err) { /* silent */ }
      finally { setLoading(false); }
    };
    fetch();
  }, []); // eslint-disable-line

  const referralLink = referralInfo ? `${window.location.origin}/auth?ref=${referralInfo.referral_code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-10 animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-4">
            <Gift size={28} className="text-[#2D6A4F]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332] mb-2" data-testid="referral-title">
            Refer a Friend, Earn $5
          </h1>
          <p className="text-[#4B6B58] max-w-md mx-auto">
            Share your unique link. When a friend signs up, you earn $5 credit. They get their first month free!
          </p>
        </div>

        {loading ? (
          <div className="text-center text-[#4B6B58] animate-pulse py-8">Loading...</div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8 animate-fade-in-delay-1">
              <div className="bg-white rounded-[1.5rem] p-5 text-center shadow-[0_4px_12px_rgb(27,67,50,0.04)]" data-testid="referral-count">
                <Users size={20} className="text-[#2D6A4F] mx-auto mb-2" />
                <span className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] block">{referralInfo?.referral_count || 0}</span>
                <span className="text-xs text-[#4B6B58]">Friends Referred</span>
              </div>
              <div className="bg-white rounded-[1.5rem] p-5 text-center shadow-[0_4px_12px_rgb(27,67,50,0.04)]" data-testid="referral-credits">
                <DollarSign size={20} className="text-[#2D6A4F] mx-auto mb-2" />
                <span className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] block">${(referralInfo?.referral_credits || 0).toFixed(2)}</span>
                <span className="text-xs text-[#4B6B58]">Credits Earned</span>
              </div>
              <div className="bg-white rounded-[1.5rem] p-5 text-center shadow-[0_4px_12px_rgb(27,67,50,0.04)]">
                <Share2 size={20} className="text-[#2D6A4F] mx-auto mb-2" />
                <span className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] block">$5</span>
                <span className="text-xs text-[#4B6B58]">Per Referral</span>
              </div>
            </div>

            {/* Share Link */}
            <div className="bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgb(27,67,50,0.06)] mb-8 animate-fade-in-delay-2" data-testid="referral-share-section">
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4">Your Referral Link</h3>
              <div className="flex gap-3">
                <input value={referralLink} readOnly data-testid="referral-link-input" className="stable-input flex-1 text-sm font-mono bg-[#F2F7F4]" />
                <button
                  onClick={copyLink}
                  data-testid="copy-referral-link"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift hover:bg-[#2D6A4F] flex-shrink-0"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
              <p className="text-xs text-[#4B6B58] mt-3">Share this link with friends. When they sign up, you both win!</p>
            </div>

            {/* How It Works */}
            <div className="bg-[#F9F7F3] rounded-[2rem] p-8 mb-8 animate-fade-in-delay-3">
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-5">How It Works</h3>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  { step: "1", title: "Share Your Link", desc: "Send your unique referral link to fellow horse owners." },
                  { step: "2", title: "Friend Signs Up", desc: "They get their first month completely free." },
                  { step: "3", title: "You Earn $5", desc: "$5 credit added to your account automatically." },
                ].map(item => (
                  <div key={item.step} className="text-center">
                    <div className="w-10 h-10 rounded-full bg-[#1B4332] text-white flex items-center justify-center mx-auto mb-3 font-bold text-sm">{item.step}</div>
                    <h4 className="font-semibold text-[#122A1F] text-sm mb-1">{item.title}</h4>
                    <p className="text-xs text-[#4B6B58]">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Referral History */}
            {history.length > 0 && (
              <div className="bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgb(27,67,50,0.06)] animate-fade-in-delay-4" data-testid="referral-history">
                <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4">Referral History</h3>
                <div className="space-y-3">
                  {history.map(ref => (
                    <div key={ref.id} className="flex items-center justify-between py-3 border-b border-[#D1E0D5] last:border-0">
                      <div>
                        <span className="font-medium text-[#122A1F] text-sm">{ref.referred_name}</span>
                        <span className="text-xs text-[#4B6B58] block">{new Date(ref.created_at).toLocaleDateString()}</span>
                      </div>
                      <span className="text-[#2D6A4F] font-bold text-sm">+${ref.credit_amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
